import _ from 'lodash';
import type { z } from 'zod';
import { Subject } from '../common/enums';
import { SearchStringSchema } from '../common/schemas';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { encodeRecordId } from '../recordIds';
import { StaffAttributes } from './constants';
import { DBStaffSchema, StaffSchema } from './schemas';

type Staff = z.infer<typeof StaffSchema>;

export const StaffCodecConfig: TransformerConfig<typeof StaffSchema, typeof DBStaffSchema> = {
  name: 'Staff',
  clientSchema: StaffSchema,
  dbSchema: DBStaffSchema,
  transformers: [
    createRenameTransformer<typeof StaffSchema, typeof DBStaffSchema>('id', 'staffId'),
    createRenameTransformer<typeof StaffSchema, typeof DBStaffSchema>('slackUserId', 'slackId'),
    createRenameTransformer<typeof StaffSchema, typeof DBStaffSchema>(
      'slackChannelIds',
      'slackChannels'
    ),

    // classes: [{ classId, subject, classCode }] ↔ classes: { "subject#classCode": {} }
    createTransformer<typeof StaffSchema, typeof DBStaffSchema>({
      encoder: {
        sourceFields: ['classes'] as const,
        encode: ({ classes }) => ({
          classes: Object.fromEntries(
            classes.map((c: Staff['classes'][number]) => [`${c.subject}#${c.classCode}`, {}])
          ),
        }),
      },
      decoder: {
        sourceFields: ['classes'] as const,
        decode: ({ classes }) => ({
          classes: Object.keys(classes ?? {}).map(key => {
            const [subject, classCode] = key.split('#') as [Subject, string];
            return {
              classId: encodeRecordId('class', { subject, code: classCode }),
              subject,
              classCode,
            };
          }),
        }),
      },
    }),

    // Handle search string updates
    createTransformer<typeof StaffSchema, typeof DBStaffSchema>({
      encoder: {
        sourceFields: StaffAttributes.Search,
        dependencies: StaffAttributes.Search,
        encode: staff => {
          let searchStr = '';
          StaffAttributes.Search.forEach(attr => {
            const val = staff[attr];
            searchStr += SearchStringSchema.parse(val);
          });
          return { searchStr, ..._.pick(staff, StaffAttributes.Search) };
        },
      },
    }),
  ],
};

export const encodeStaffToDb = createEncodeToDbFn(StaffCodecConfig);
export const decodeStaffFromDb = createDecodeFromDbFn(StaffCodecConfig);
